import {
  AssetsProvider,
  Image,
  mobile,
  styled,
  useAssetsPath,
  useQuery,
  useTranslation,
} from '@obvio/app'
import { Button, Grid, GridItem, Loading, Stack, Tab } from '@obvio/ui'

import { TabsMaxWidth } from './Tabs'
import { CenteredSection } from '../CenteredSection'
import { getImage } from '../utils'
import { QUERIES } from '@/constants/queries'

import type { ImageAsset } from '@obvio/app'
import type { ReactElement } from 'react'

type Package = {
  id: string
  title: string
  image: ImageAsset
  numberOfNights: number
  startingPrice: number
  slug: string
  profitroomId: string
}

type PackagesElementsProps = {
  packages: Package[]
}

const PackageWrap = styled.div<{ $bgUrl: string }>`
  height: clamp(20rem, 40vw, 27.25rem);
  width: 100%;
  display: block;
  grid-template-rows: 3fr 1fr;
  position: relative;
  color: ${(theme) => theme.colors.primary};

  overflow: hidden;
  img {
    transition: 500ms all;
  }
  :hover {
    img {
      transform: scale(1.1);
    }
    .sp-price {
      display: block;
    }
    .sp-days,
    .sp-title {
      display: none;
    }
  }
`

const Title = styled.span`
  font-size: 1.25rem;
  font-weight: 500;
  text-align: center;
`

const GridGapHorizontal = styled(Grid)`
  gap: ${(theme) => theme.spacing.big};

  @media ${mobile} {
    grid-template-columns: 1fr;
    padding: 0;
  }
`
const InfoWrap = styled(Stack)`
  ${(theme) => theme.flexCenter};
  flex-direction: column;
  z-index: 2;
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
`

const Days = styled.span`
  font-size: 1rem;
  font-weight: 300;
  color: ${(theme) => theme.colors.primary};
  margin-top: ${(theme) => theme.spacing.big};
`

const Price = styled.span`
  display: none;
  z-index: 2;
  font-size: 1rem;
  font-weight: 300;
  > span {
    font-size: 3rem;
  }
`

const Check = styled(GridItem)`
  z-index: 2;
  > button {
    color: ${(theme) => theme.colors.primary};
  }
`

type SinglePackageProps = {
  package: Package
}

function SinglePackage({ package: singlePackage }: SinglePackageProps) {
  const bgUrl = useAssetsPath(singlePackage.id, singlePackage.image.name)
  const { numberOfNights, image, id, startingPrice, title, slug } =
    singlePackage

  return (
    <PackageWrap $bgUrl={bgUrl}>
      <Image img={getImage(image)} context={id} layout="fill" />
      <InfoWrap kind="vertical">
        <Price className="sp-price">
          od <span>{startingPrice}</span> zł
        </Price>
        <Title className="sp-title">{title}</Title>
        <Days className="sp-days">
          {numberOfNights}{' '}
          {numberOfNights === 1 ? 'NOC' : numberOfNights > 5 ? 'NOCY' : 'NOCE'}
        </Days>
        <Check>
          <Button kind="transparent" href={`/pakiety/${slug}`}>
            SPRAWDŹ
          </Button>
        </Check>
      </InfoWrap>
    </PackageWrap>
  )
}

function PackagesElements({ packages }: PackagesElementsProps) {
  return (
    <GridGapHorizontal templateColumns="repeat(3, minmax(0,1fr))">
      {packages.map((singlePackage) => (
        <GridItem key={singlePackage.id}>
          <SinglePackage package={singlePackage} />
        </GridItem>
      ))}
    </GridGapHorizontal>
  )
}

export function Packages(): ReactElement {
  const { t } = useTranslation()

  const { data, loading } = useQuery('getProfitroomOfferCategories', {
    select: QUERIES.packages.getProfitroomOfferCategories ?? {},
  })

  if (loading) {
    return (
      <CenteredSection>
        <Loading />
      </CenteredSection>
    )
  }

  return (
    <AssetsProvider path="/offer">
      <CenteredSection>
        <TabsMaxWidth>
          <Tab key="all" label={t('label.all')}>
            <PackagesElements
              packages={
                data?.reduce<Package[]>(
                  (acc, category) => [...acc, ...category.elements],
                  [],
                ) ?? []
              }
            />
          </Tab>
          {data?.map((category) => (
            <Tab key={category.id} label={category.title}>
              <PackagesElements packages={category.elements} />
            </Tab>
          ))}
        </TabsMaxWidth>
      </CenteredSection>
    </AssetsProvider>
  )
}
